import styled from "@emotion/styled";

import Card from "shared/components/Card";
import FlexContainer from "shared/components/FlexContainer";
import Icon from "shared/components/Icon";
import { COLORS, MEDIA_QUERIES } from "shared/config/constants";

/** PRODUCT-2520 AC: five compact cards stay on one row at ~1100px+, wrap below. */
const COMPACT_FILTER_CARDS_SINGLE_ROW = "@media all and (min-width: 1100px)";
const COMPACT_FILTER_CARDS_WRAP = "@media all and (max-width: 1099px)";

const Container = styled(FlexContainer)`
  position: relative;
  flex-direction: column;

  ${MEDIA_QUERIES.w.ltMd} {
    padding: 0 24px;
  }
`;

const Title = styled.h1`
  color: ${COLORS.darkViolet3};
  font-weight: 500;
  margin-bottom: 0 !important;

  ${MEDIA_QUERIES.w.ltMd} {
    display: none;
  }
`;

const CardGroup = styled(FlexContainer, {
  shouldForwardProp: (prop: string) => prop !== "$compact",
})<{ $compact?: boolean }>`
  flex-wrap: wrap;
  row-gap: 16px;
  column-gap: 16px;
  padding: 24px 0;
  ${({ $compact }) =>
    $compact &&
    `
    ${COMPACT_FILTER_CARDS_SINGLE_ROW} {
      flex-wrap: nowrap;
    }
  `}
`;

interface FilterCardProps {
  isActive?: boolean;
  isAccent?: boolean;
  $compact?: boolean;
}

const StyledFilterCard = styled(Card, {
  shouldForwardProp: (prop: string) =>
    !["isActive", "isAccent", "$compact"].includes(prop),
})<FilterCardProps>`
  cursor: ${(props) => (props.isActive ? "default" : "pointer")};
  color: ${COLORS.primary};
  min-width: ${(props) => (props.$compact ? "160px" : "250px")};
  width: ${(props) => (props.$compact ? "auto" : "250px")} !important;
  flex: ${(props) => (props.$compact ? "1 1 160px" : "0 0 auto")};
  min-height: 77px;
  height: auto !important;
  padding: 12px !important;
  border-radius: 8px;
  border: ${(props) =>
    props.isActive || props.isAccent ? "1px solid" : "none"};
  border-color: ${(props) =>
    props.isActive
      ? COLORS.violetTheme.lightBlueViolet
      : props.isAccent
      ? COLORS.warning
      : "none"};
  box-shadow: ${(props) => (props.isActive ? COLORS.boxShadowDeep : "none")};

  ${COMPACT_FILTER_CARDS_WRAP} {
    ${(props) =>
      props.$compact &&
      `
      flex: 1 1 calc(50% - 16px);
      min-width: 160px;
    `}
  }

  .ant-card-body {
    height: 100%;

    .filter-content {
      display: flex;
      column-gap: 15px;
      height: 100%;

      .filter-count {
        display: flex;
        font-size: 28px;
        font-weight: 400;
        justify-content: center;
        line-height: 28px;
        letter-spacing: 0em;
        text-align: left;
        align-items: center;
        width: 80px;
        transition: opacity 0.5s, transform 0.5s;
        opacity: 1;
      }

      .filter-count.loading {
        opacity: 0;
      }

      .filter-description {
        width: 100%;

        .filter-notice {
          display: flex;
          justify-content: space-between;
          font-size: 10px;
          font-weight: 400;
          line-height: 15px;
          letter-spacing: 0em;
          text-align: left;
          color: ${(props) =>
            props.isActive ? COLORS.violetTheme.green : COLORS.primary};

          &.active {
            font-weight: 600;
          }
        }

        .filter-type {
          font-size: 15px;
          font-weight: 400;
          line-height: 19px;
          letter-spacing: 0em;
          text-align: left;
        }
      }
    }
  }

  :hover {
    box-shadow: ${COLORS.boxShadowDeep};
  }
`;

const StyledCloseIcon = styled(Icon.CloseOneIcon)`
  width: 14px;
  height: 14px;
  cursor: pointer;
`;

export { Container, Title, CardGroup, StyledFilterCard, StyledCloseIcon };
